/**
 * useDismissable — the one dismissal behaviour for every popup and modal
 * (sweep 2 Pass D, #243).
 *
 * Before this hook, outside-click detection existed eleven times in three
 * flavours (document pointerdown, document mousedown, and a `fixed inset-0`
 * click-catcher div), and five popups had no Escape path at all. Every
 * dismissable surface now attaches the same two listeners while open:
 *
 * - pointerdown, routed by `pointer`: outside a set of refs (the house
 *   popups), anywhere (the tap-opened Tooltip), or not at all (modals — the
 *   backdrop owns click-to-close per DESIGN-SYSTEM.md Modals);
 * - Escape, which only ever closes the TOPMOST open surface. Open instances
 *   form a stack in open order, so Escape over a Select open inside a modal
 *   closes the Select and leaves the modal alone — instead of both racing on
 *   their own window listeners and closing together.
 *
 * Positioning stays the caller's (the house `relative` + `absolute top-full`
 * pattern, or a portal); this hook owns only dismissal. The DESIGN.md
 * "Select stays out of ResponsiveTable cells" carve-out is untouched here —
 * reopening it is a named future decision that needs a shared positioning
 * strategy, not a dismissal hook.
 */
import { useEffect, useRef } from "react";

/** Structural ref type so any element ref (div, input, span) qualifies. */
type InsideRef = { readonly current: Element | null };

export interface DismissableOptions {
  /** Whether the surface is open. Listeners attach only while true. */
  open: boolean;
  /** Close the surface. Runs on pointer dismissal, and on Escape unless
   *  `onEscape` overrides it. */
  onDismiss: () => void;
  /** Escape-specific close, for surfaces that also return focus to their
   *  trigger on Escape but deliberately not on an outside click (the user
   *  clicked elsewhere on purpose). Defaults to `onDismiss`. */
  onEscape?: () => void;
  /**
   * Pointer dismissal:
   * - an array of refs: a pointerdown outside every ref dismisses;
   * - `"any"`: every pointerdown dismisses (the tap-opened Tooltip);
   * - `"none"`: Escape only (modals — their backdrop owns click-to-close).
   */
  pointer: ReadonlyArray<InsideRef> | "any" | "none";
}

/** Open surfaces in open order; Escape only ever pops the top. */
const escapeStack: Array<{ run: () => void }> = [];

export function useDismissable({
  open,
  onDismiss,
  onEscape,
  pointer,
}: DismissableOptions): void {
  // Latest callbacks/refs without re-attaching listeners every render.
  const dismissRef = useRef(onDismiss);
  const escapeRef = useRef(onEscape ?? onDismiss);
  const pointerRef = useRef(pointer);
  useEffect(() => {
    dismissRef.current = onDismiss;
    escapeRef.current = onEscape ?? onDismiss;
    pointerRef.current = pointer;
  });

  useEffect(() => {
    if (!open) return;

    const entry = { run: () => escapeRef.current() };
    escapeStack.push(entry);

    // On window, not document: tests and callers fire keydown on either, and
    // document events bubble to window while the reverse never happens.
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (escapeStack[escapeStack.length - 1] !== entry) return;
      entry.run();
    };

    const onPointerDown = (e: PointerEvent) => {
      const mode = pointerRef.current;
      if (mode === "none") return;
      if (
        mode !== "any" &&
        mode.some((r) => r.current?.contains(e.target as Node))
      ) {
        return;
      }
      dismissRef.current();
    };

    window.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      const i = escapeStack.indexOf(entry);
      if (i >= 0) escapeStack.splice(i, 1);
      window.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [open]);
}
